import {
  useCallback, useRef, useState, useTransition,
} from 'react';
import type { DisabledState } from '~/_shared/types/disabledState';
import type { MultiPolygon } from '~/_shared/types/polygon/polygon.types';
import {
  isDriveTimePolygon, isIndividualRadius, isRadiusProximity, type Proximity,
} from '~/_shared/types/proximity/proximity.types';
import type { SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { useTranslation } from '~/_shared/utils/hooks';
import { getCachedNumberFormatter } from '~/_shared/utils/numberFormatter/numberFormatter';
import { getProximityRadius } from '~/_shared/utils/proximity/getProximityRadius';
import { getNiceDistanceLimit } from '~/_shared/utils/unitSystem/niceLimit.helpers';
import { getUnitSymbols } from '~/_shared/utils/unitSystem/unitSystem.helpers';
import {
  AGS_REPORT_MAX_RADIUS_IN_MILES, REPORT_VALIDATION_ERROR_CODES, validatePolygonForAgsApi, validateRadiusForAgsApi,
} from '~/exportReport/validation/exportReportApiValidation.helpers';
import { getExportReportAreas } from '~/exportReport/validation/getExportReportAreas';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import { useMapSettingsSettingsUnitSystemSelector } from '~/store/mapSettings/settings/mapSettingsSettings.selectors';

const DISABLED_DEFAULT: DisabledState = { isDisabled: true, reason: '' };
// A failed area lookup must not block the user. We will allow user to select the area.
const VALIDATION_FAILED: DisabledState = { isDisabled: false };

const formatLimit = (limit: number) => getCachedNumberFormatter({}).format(limit);

const useValidationRunner = () => {
  const [isPending, startTransition] = useTransition();
  const [validation, setValidation] = useState<DisabledState>(DISABLED_DEFAULT);
  const latestRequestIdRef = useRef(0);

  // Keeps the most recently started validation authoritative and never rejects, so awaiting the result is always safe.
  const run = useCallback((task: () => Promise<DisabledState>): Promise<DisabledState> => {
    const requestId = ++latestRequestIdRef.current;
    const promise = task().catch(() => VALIDATION_FAILED);

    startTransition(async () => {
      const result = await promise;

      if (requestId === latestRequestIdRef.current) {
        setValidation(result);
      }
    });

    return promise;
  }, []);

  return { isPending, validation, run };
};

export const useExportReportValidation = () => {
  const [t] = useTranslation();
  const latLngLookup = useLatLngSpreadsheetData();
  const mapUnitSystem = useMapSettingsSettingsUnitSystemSelector();
  const { isPending, validation, run } = useValidationRunner();

  const validateReportAreasContainingPolygon = useCallback((multiPolygon: MultiPolygon): Promise<DisabledState> => {
    return getExportReportAreas({ type: 'polygon', paths: multiPolygon })
      .then((reportAreas): DisabledState => {
        return reportAreas.length
          ? { isDisabled: false }
          : {
            isDisabled: true,
            reason: t('exportReport.unavailableInTheArea'),
            reasonCode: REPORT_VALIDATION_ERROR_CODES.AreaNotAvailable,
          };
      });
  }, [t]);

  const validateReportMultiPolygon = useCallback((multiPolygon: MultiPolygon): Promise<DisabledState> => (
    run(async () => {
      const polygonValidationResult = validatePolygonForAgsApi(multiPolygon);

      if (!polygonValidationResult.isValid) {
        return {
          isDisabled: true,
          reason: t(polygonValidationResult.reason),
          reasonCode: polygonValidationResult.errorCode,
        };
      }

      return validateReportAreasContainingPolygon(multiPolygon);
    })
  ), [run, t, validateReportAreasContainingPolygon]);

  const validateReportProximity = useCallback((proximity: Proximity, spreadsheetRowId?: SpreadsheetRowId): Promise<DisabledState> => {
    if (isDriveTimePolygon(proximity)) {
      return (async () => {
        if (!proximity.data.paths) {
          return {
            isDisabled: true,
            reason: t('exportReport.unavailableInTheArea'),
            reasonCode: REPORT_VALIDATION_ERROR_CODES.AreaNotAvailable,
          } as const;
        }

        return validateReportMultiPolygon(proximity.data.paths);
      })();
    }

    // a spreadsheet row's location, when available, takes priority over the proximity's own center
    const spreadsheetCenter = spreadsheetRowId ? latLngLookup.getRow(spreadsheetRowId) : undefined;
    const proximityCenter = isIndividualRadius(proximity) ? proximity.data.data : undefined;
    const center = spreadsheetCenter ?? proximityCenter;

    const validateDistance = (): DisabledState => {
      if (isRadiusProximity(proximity) && !validateRadiusForAgsApi(proximity, spreadsheetRowId)) {

        const radiusUnit = getProximityRadius(proximity, spreadsheetRowId)?.unit ?? mapUnitSystem;
        const { unit } = getUnitSymbols(radiusUnit);
        return {
          isDisabled: true,
          reason: t('exportReport.unavailableRadiusOverLimit', {
            limit: `${formatLimit(getNiceDistanceLimit(AGS_REPORT_MAX_RADIUS_IN_MILES, radiusUnit))} ${unit}`,
          }),
          reasonCode: REPORT_VALIDATION_ERROR_CODES.RadiusTooLarge,
        };
      }

      return { isDisabled: false };
    };

    return run(async () => {
      if (!center) {
        return validateDistance();
      }

      const reportAreas = await getExportReportAreas({ type: 'point', point: center });

      return reportAreas.length
        ? validateDistance()
        : {
          isDisabled: true,
          reason: t('exportReport.notSupportedArea.radii'),
          reasonCode: REPORT_VALIDATION_ERROR_CODES.RadiiNotSupportedInArea,
        };
    });
  }, [latLngLookup, run, validateReportMultiPolygon, t, mapUnitSystem]);

  const validateReportLocation = useCallback((spreadsheetRowId: SpreadsheetRowId): Promise<DisabledState> => (
    run(async () => {
      const center = latLngLookup.getRow(spreadsheetRowId);

      if (!center) {
        return { isDisabled: false };
      }

      const reportAreas = await getExportReportAreas({ type: 'point', point: center });

      return reportAreas.length
        ? { isDisabled: false }
        : {
          isDisabled: true,
          reason: t('exportReport.unavailableInTheArea'),
          reasonCode: REPORT_VALIDATION_ERROR_CODES.AreaNotAvailable,
        };
    })
  ), [run, t, latLngLookup]);

  const reportValidationResult: DisabledState = isPending
    ? { isDisabled: true, reason: t('Loading...') }
    : validation;

  return {
    validateReportProximity,
    validateReportMultiPolygon,
    validateReportLocation,
    reportValidationResult,
  };
};
